import React, { FC, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { updatePassword } from "../../Services/Password";
import { validatePassword } from "../../utils/passwordValidation";
import AuthContext from "../../store/AuthContext";

interface IModifyPasswordProp {
  firstTime?: boolean;
}

const ModifyPassword: FC<IModifyPasswordProp> = ({ firstTime }) => {
  const { userName, code } = useParams<Record<string, string | undefined>>();
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const { loggedPerson } = useContext(AuthContext);

  useEffect(() => {
    if (loggedPerson) {
      navigate("/settings/change-password");
    }
  }, [loggedPerson, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid || newPassword !== confirmPassword) {
      return;
    }

    try {
      if (!userName || !code) {
        return;
      }

      await updatePassword(
        newPassword,
        confirmPassword,
        userName,
        code,
        !!firstTime,
      );
      navigate("/login");
    } catch (error) {
      console.error(error);
    }
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = event.target.value;
    setNewPassword(newPassword);

    const isPasswordValid =
      newPassword.trim() === newPassword &&
      newPassword.length >= 8 &&
      /[A-Z]/.test(newPassword) &&
      /\d/.test(newPassword) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(newPassword) &&
      /^[^\s!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    setIsPasswordValid(isPasswordValid);
  };

  const handleConfirmPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConfirmPasswordTouched(true);
    const password = event.target.value;
    setConfirmPassword(password);
  };

  return (
    <div className="min-h-[85vh] w-full max-w-md sm:max-w-lg md:w-[560px] flex flex-col justify-center items-center self-center">
      <div
        className={`flex w-full align-middle justify-center gap-28 px-5 py-4 bg-[#9C3325]`}
      >
        <h2 className={`text-center text-xl font-sans text-white roboto-font`}>
          CHANGE PASSWORD
        </h2>
      </div>
      <div className="bg-white w-full py-4">
        <form
          className="text-black py-8 flex flex-col gap-4"
          onSubmit={handleSubmit}
        >
          <div className="w-full px-5">
            <input
              type="password"
              placeholder="Enter New Password"
              value={newPassword}
              onChange={handlePasswordChange}
              className="w-full p-2 border border-gray-300 rounded"
            />
            <ul className="text-left list-item mt-2">
              <li
                className={
                  newPassword.length >= 8
                    ? "text-green-500"
                    : "text-feedback-error"
                }
              >
                - Minimum 8 characters
              </li>
              <li
                className={
                  /[A-Z]/.test(newPassword)
                    ? "text-green-500"
                    : "text-feedback-error"
                }
              >
                - At least 1 Capital Letter
              </li>
              <li
                className={
                  /[a-z]/.test(newPassword)
                    ? "text-green-500"
                    : "text-feedback-error"
                }
              >
                - At least 1 Lowercase Letter
              </li>
              <li
                className={
                  /\d/.test(newPassword)
                    ? "text-green-500"
                    : "text-feedback-error"
                }
              >
                - At least 1 number
              </li>
              <li
                className={
                  /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
                    ? "text-green-500"
                    : "text-feedback-error"
                }
              >
                - At least 1 special character
              </li>
            </ul>
          </div>
          <div className="w-full px-5">
            <input
              type="password"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              className="w-full p-2 border border-gray-300 rounded"
            />
            {confirmPasswordTouched && (
              <span
                className={`${newPassword !== confirmPassword ? "text-feedback-error" : "text-green-500"}`}
              >
                Passwords{" "}
                {newPassword !== "" && newPassword === confirmPassword
                  ? "match!"
                  : "do not match!"}
              </span>
            )}
          </div>
          <button
            type="submit"
            disabled={!isPasswordValid || newPassword !== confirmPassword}
            className={`cursor-pointer w-11/12 text-white mx-auto flex text-center align-middle justify-center py-4 inter_font bg-[#BC3323] font-semibold text-[1rem] ${!isPasswordValid || newPassword !== confirmPassword ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            Change Password
          </button>
        </form>
      </div>
    </div>
  );
};

export default ModifyPassword;
